import { MapPin, Users, PoundSterling } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { format, parseISO } from "date-fns";
import { formatEventPrice, formatPrice } from "../../lib/utils";
import { EventScarcityBanner } from "./EventScarcityBanner";
import type { Event } from "../../lib/types";
import { useEventPricePoints } from "../../lib/hooks/useEventPricePoints";
import { EventSchedule } from "./EventSchedule";
import { EventPriceTable } from "./EventPriceTable";
import { buildEventImageAttributes } from "../../lib/event-image-urls";

interface EventInfoCardProps {
  event: Event;
  getTagVariant: (tagName: string) => "default" | "success" | "warning" | "error" | "info";
}

export function EventInfoCard({ event, getTagVariant }: EventInfoCardProps) {
  const {
    availablePricePoints,
    futurePricePoints,
    groupedByLabel,
    hasAnyNotes,
    hasFuturePricePoints,
    bookingEnd,
  } = useEventPricePoints(event);

  const heroImg = buildEventImageAttributes(event.imageUrl, "detail");

  return (
    <>
      {/* Event Image */}
      <div className="relative min-h-[16rem] md:min-h-[24rem]">
        <img
          src={heroImg.src}
          srcSet={heroImg.srcSet}
          sizes={heroImg.sizes}
          width={heroImg.width}
          height={heroImg.height}
          alt={event.title}
          className="w-full h-64 md:h-96 object-cover rounded-lg"
          decoding="async"
        />
        <div className="absolute bottom-4 left-4 flex gap-2">
          {(event.tags || []).map((tagName) => (
            <Badge key={tagName} variant={getTagVariant(tagName)}>
              {tagName}
            </Badge>
          ))}
        </div>
      </div>

      {/* Event Details */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1">
              <CardTitle className="text-2xl">{event.title}</CardTitle>
            </div>
            <div className="shrink-0">
              {event.isCancelled ? (
                <Badge variant="error" size="sm">
                  Cancelled
                </Badge>
              ) : (
                <EventScarcityBanner event={event} variant="badge" />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <EventSchedule event={event} />

          {availablePricePoints.length === 1 &&
          futurePricePoints.length === 0 ? (
            (() => {
              const pp = availablePricePoints[0];
              const hasDeposit =
                pp.depositCents !== null &&
                pp.depositCents !== undefined &&
                pp.balanceDueAt !== null &&
                pp.balanceDueAt !== undefined &&
                new Date(pp.balanceDueAt) > new Date();

              return (
                <div className="space-y-2">
                  <div className="flex items-center text-gray-600">
                    <PoundSterling className="w-5 h-5 mr-3" />
                    <span>
                      {formatPrice(pp.priceCents, pp.currency)}
                    </span>
                  </div>
                  {hasDeposit && (
                    <div className="ml-8 space-y-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="w-20 font-medium">Deposit:</span>
                        <span>
                          {formatPrice(pp.depositCents!, pp.currency)} due now
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="w-20 font-medium">Balance:</span>
                        <span>
                          {formatPrice(
                            pp.priceCents - pp.depositCents!,
                            pp.currency,
                          )}{" "}
                          due on{" "}
                          {format(parseISO(pp.balanceDueAt!), "MMM do, yyyy")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="flex items-center text-gray-900">
              <PoundSterling className="w-5 h-5 mr-3" />
              <span>
                {formatEventPrice(
                  event.pricePoints,
                  event.bookingClosesAt ?? null,
                  event.startsAt,
                )}
              </span>
            </div>
          )}

          <div className="flex items-center text-gray-600">
            <MapPin className="w-5 h-5 mr-3" />
            <span>
              {(event as { location?: string | null }).location ||
                "Sailing Club"}
            </span>
          </div>

          <div className="flex items-center text-gray-600">
            <Users className="w-5 h-5 mr-3" />
            <span>
              {event.audience === "MEMBERS_ONLY" ? "Members only" : "Open"}
            </span>
          </div>

          <div className="pt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
            <p className="text-gray-700">{event.description}</p>
          </div>

          {event.schedule && (
            <div className="pt-4">
              <h3 className="font-semibold text-gray-900 mb-2">Schedule</h3>
              <p className="text-gray-700 whitespace-pre-line">
                {event.schedule}
              </p>
            </div>
          )}

          {event.whatToBring && (
            <div className="pt-4">
              <h3 className="font-semibold text-gray-900 mb-2">What to Bring</h3>
              <p className="text-gray-700 whitespace-pre-line">
                {event.whatToBring}
              </p>
            </div>
          )}

          {/* Price Points Table */}
          {(availablePricePoints.length > 1 || futurePricePoints.length > 0) && (
            <EventPriceTable
              groupedByLabel={groupedByLabel}
              hasAnyNotes={hasAnyNotes}
              hasFuturePricePoints={hasFuturePricePoints}
              bookingEnd={bookingEnd}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}
